import { useEffect } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { ASSETS_QUERY, ASSET_GROUPS_QUERY } from '@scio3/graphql-client';
import { useTeamStore } from '@/stores/team-store';
// Direct module paths, NOT the `FolderItemList` / feature barrels: this hook is mounted from
// AppShell and therefore lives in the main bundle. Importing a barrel would drag the page's
// component tree in with it and undo the code splitting this prefetch exists to hide.
import { resolveItemPageSize, resolveFolderPageSize } from '@/components/shared/FolderItemList/dev';
import { buildQueryInput } from '@/features/assets/utils/build-query-input';
import { buildSortInput, buildGroupSortInput } from '@/features/assets/utils/build-sort-input';
import { useAssetSortStore } from '@/features/assets/stores/asset-sort-store';

/**
 * Default `pageSize` argument of `useAssets` (its module-local `DEFAULT_PAGE_SIZE`).
 * `AssetsPage` calls `useAssets(currentPath, folderTeamId)` without overriding it.
 *
 * Duplicated deliberately — importing `useAssets` here would pull the whole hook, and its
 * transitive feature imports, into the main bundle. `useAssetsPrefetch.test.ts` asserts these
 * variables stay identical to the ones the page actually issues, so drift fails a test rather
 * than silently degrading into a prefetch that warms the wrong cache key.
 */
const ASSETS_DEFAULT_PAGE_SIZE = 50;

/**
 * Idle-time prefetch for the Assets list.
 *
 * Companion to `prefetchRouteChunks` (`lib/prefetch-routes.ts`) which warms the JS chunk.
 * This hook warms the Apollo cache for the two queries `useAssets` fires on a cold root
 * visit — the items query AND the root-folders query. Prefetching only the items query
 * leaves the first paint still waiting on folders, so both are needed to actually make the
 * click feel instant (see #1229).
 *
 * `cache-first` so the prefetch is a no-op when the cache is already warm (e.g. after
 * IndexedDB hydration on a returning session). `requestIdleCallback` so we don't compete
 * with whatever the user is currently doing on Screens / Playlists / etc.
 *
 * The variables below MUST match `useAssets` byte-for-byte or Apollo stores them under a
 * different cache key and the prefetch becomes an invisible no-op — the same query is issued
 * again on click. That is why this reads the real `build*Input` builders and `resolve*PageSize`
 * helpers instead of hand-rolling the shapes.
 *
 * Safe to assume defaults: the asset FILTER store is memory-only (plain `create()`), so on the
 * hard reload where prefetch matters it is always at defaults. The SORT store persists to
 * localStorage and is read synchronously at module init, so there is no hydration race.
 *
 * Mount once from `AppShell` after auth resolves.
 */
export function useAssetsPrefetch(): void {
  const client = useApolloClient();
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const sortField = useAssetSortStore((s) => s.sortField);
  const sortDirection = useAssetSortStore((s) => s.sortDirection);

  useEffect(() => {
    if (!activeTeamId) return;

    const run = () => {
      // Root path, default filters — what the user gets when they click the nav item.
      // `teamId` is intentionally NOT passed: `buildQueryInput` only sets `query.teamId`
      // when the resolved path is truthy, and root resolves to `null`.
      const queryInput = buildQueryInput(
        { fileType: [], tags: [], searchText: '', searchScope: 'folder' },
        '/',
        undefined,
        false,
      );

      client
        .query({
          query: ASSETS_QUERY,
          variables: {
            first: resolveItemPageSize(ASSETS_DEFAULT_PAGE_SIZE),
            teamId: activeTeamId,
            query: queryInput,
            sort: buildSortInput(sortField, sortDirection),
            // Assets always sends this key (`{}` when not in the Favorites location).
            // Playlists/Schedules OMIT it — `{}` and absent are different cache keys.
            extendParams: {},
          },
          fetchPolicy: 'cache-first',
        })
        .catch(() => {
          // Best-effort — don't surface errors to the user.
        });

      // Root folders. `extensions.groups` doesn't resolve folders at root, so the page
      // issues this separate query (useAssets.ts:316) — prefetch it too or the first paint
      // still blocks on folders.
      client
        .query({
          query: ASSET_GROUPS_QUERY,
          variables: {
            first: resolveFolderPageSize(),
            teamId: activeTeamId,
            // At root, `query.teamId` is omitted and `path` is null.
            query: { type: 'ASSET', path: null },
            sort: buildGroupSortInput(sortField, sortDirection),
          },
          fetchPolicy: 'cache-first',
        })
        .catch(() => {});
    };

    // Cleanup is intentionally omitted — the prefetch fires at most once per mount and the
    // underlying `client.query` is cancellation-resilient. Mirrors `useMdmPrefetch`.
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run);
    } else {
      // Safari fallback
      setTimeout(run, 2000);
    }
  }, [client, activeTeamId, sortField, sortDirection]);
}
